import store from 'store'

class RedesignMegaNav {
  constructor (root) {
    this.root = root
    this.topTabs = Array.from(this.root.querySelectorAll('[data-top-tab]'))
    this.topItems = Array.from(this.root.querySelectorAll('[data-top-tab-item]'))
    this.subItems = Array.from(this.root.querySelectorAll('[data-sub-tab-item]'))
    this.panels = Array.from(this.root.querySelectorAll('[data-panel-for]'))
    this.activeTab = null
    this.activeSub = null
    this.lastTrigger = null
    this.restoringFocus = false
    this.openTimer = null
    this.closeTimer = null
    this.HOVER_IN_DELAY = 50
    this.HOVER_OUT_DELAY = 150
    this.mediaQuery = window.matchMedia('(min-width: 1280px)')
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
    this.onTopTabPointerEnter = this.onTopTabPointerEnter.bind(this)
    this.onTopTabFocusIn = this.onTopTabFocusIn.bind(this)
    this.onTopTabClick = this.onTopTabClick.bind(this)
    this.onSubTabPointerEnter = this.onSubTabPointerEnter.bind(this)
    this.onSubTabFocusIn = this.onSubTabFocusIn.bind(this)
    this.onSubTabClick = this.onSubTabClick.bind(this)
    this.onRootPointerLeave = this.onRootPointerLeave.bind(this)
    this.onRootPointerEnter = this.onRootPointerEnter.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onOverlayOpen = this.onOverlayOpen.bind(this)
    this.onMediaChange = this.onMediaChange.bind(this)
    this.onWindowResize = this.onWindowResize.bind(this)
  }

  init () {
    if (this.topTabs.length === 0) return

    window.addEventListener('resize', this.onWindowResize)

    this.topItems.forEach((item) => {
      item.addEventListener('pointerenter', this.onTopTabPointerEnter)
      item.addEventListener('focusin', this.onTopTabFocusIn)
    })

    this.topTabs.forEach((tab) => {
      tab.addEventListener('click', this.onTopTabClick)
    })

    this.subItems.forEach((item) => {
      item.addEventListener('pointerenter', this.onSubTabPointerEnter)
      item.addEventListener('focusin', this.onSubTabFocusIn)
    })

    this.root.querySelectorAll('[data-sub-tab]').forEach((tab) => {
      tab.addEventListener('click', this.onSubTabClick)
    })

    this.root.addEventListener('pointerleave', this.onRootPointerLeave)
    this.root.addEventListener('pointerenter', this.onRootPointerEnter)
    document.addEventListener('keydown', this.onDocumentKeydown)
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('redesign:overlay-open', this.onOverlayOpen)

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.addListener === 'function') {
      this.mediaQuery.addListener(this.onMediaChange)
    }
  }

  destroy () {
    this.clearTimers()
    this.close({ restoreFocus: false })

    this.topItems.forEach((item) => {
      item.removeEventListener('pointerenter', this.onTopTabPointerEnter)
      item.removeEventListener('focusin', this.onTopTabFocusIn)
    })

    this.topTabs.forEach((tab) => {
      tab.removeEventListener('click', this.onTopTabClick)
    })

    this.subItems.forEach((item) => {
      item.removeEventListener('pointerenter', this.onSubTabPointerEnter)
      item.removeEventListener('focusin', this.onSubTabFocusIn)
    })

    this.root.querySelectorAll('[data-sub-tab]').forEach((tab) => {
      tab.removeEventListener('click', this.onSubTabClick)
    })

    this.root.removeEventListener('pointerleave', this.onRootPointerLeave)
    this.root.removeEventListener('pointerenter', this.onRootPointerEnter)
    document.removeEventListener('keydown', this.onDocumentKeydown)
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('redesign:overlay-open', this.onOverlayOpen)

    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.removeListener === 'function') {
      this.mediaQuery.removeListener(this.onMediaChange)
    }

    window.removeEventListener('resize', this.onWindowResize)
  }

  clearTimers () {
    window.clearTimeout(this.openTimer)
    window.clearTimeout(this.closeTimer)
    this.openTimer = null
    this.closeTimer = null
  }

  getFirstSubTab (tabHandle) {
    const panel = this.root.querySelector('[data-panel-for="'.concat(tabHandle, '"]'))

    if (!panel) return null

    const firstSubTab = panel.querySelector('[data-sub-tab]')
    return firstSubTab ? firstSubTab.dataset.subTab : null
  }

  open (tabHandle, subHandle, trigger) {
    if (!this.mediaQuery.matches) return

    const panel = this.root.querySelector('[data-panel-for="'.concat(tabHandle, '"]'))
    const hasSubPanels = panel && panel.querySelectorAll('[data-sub-panel-for]').length > 0
    const nextSubHandle = subHandle || this.getFirstSubTab(tabHandle)

    if (!panel || (hasSubPanels && !nextSubHandle)) return

    this.clearTimers()
    this.activeTab = tabHandle
    this.activeSub = nextSubHandle
    this.lastTrigger = trigger || this.root.querySelector('[data-top-tab="'.concat(tabHandle, '"]'))

    this.topTabs.forEach((tab) => {
      const isActive = tab.dataset.topTab === tabHandle
      tab.setAttribute('aria-expanded', isActive ? 'true' : 'false')
      tab.parentElement.classList.toggle('is-active', isActive)
    })

    this.panels.forEach((item) => {
      const isActive = item.dataset.panelFor === tabHandle
      item.hidden = !isActive
    })

    this.root.querySelectorAll('[data-sub-tab]').forEach((tab) => {
      const isActive = hasSubPanels && tab.dataset.parentTab === tabHandle && tab.dataset.subTab === nextSubHandle
      tab.setAttribute('aria-expanded', isActive ? 'true' : 'false')
      tab.parentElement.classList.toggle('is-active', isActive)
    })

    this.root.querySelectorAll('[data-sub-panel-for]').forEach((subPanel) => {
      subPanel.hidden = !hasSubPanels || subPanel.dataset.parentTab !== tabHandle || subPanel.dataset.subPanelFor !== nextSubHandle
    })

    this.updatePanelAvailableHeight(panel)

    document.documentElement.classList.add('redesign-menu-open')
    document.body.classList.add('redesign-menu-open')

    if (store && store.state) {
      if (store.state.global && store.state.global.searchOpened) {
        store.commit('global/closeSearch')
      }

      if (store.state.cart && store.state.cart.open) {
        store.commit('cart/closeCart')
      }
    }

    document.dispatchEvent(new CustomEvent('redesign:overlay-open', {
      detail: { source: 'mega-nav' }
    }))
  }

  close (options = {}) {
    const restoreFocus = typeof options.restoreFocus === 'undefined' ? true : options.restoreFocus

    this.clearTimers()
    this.activeTab = null
    this.activeSub = null

    this.topTabs.forEach((tab) => {
      tab.setAttribute('aria-expanded', 'false')
      tab.parentElement.classList.remove('is-active')
    })

    this.root.querySelectorAll('[data-sub-tab]').forEach((tab) => {
      tab.setAttribute('aria-expanded', 'false')
      tab.parentElement.classList.remove('is-active')
    })

    this.root.querySelectorAll('[data-sub-panel-for]').forEach((panel) => {
      panel.hidden = true
    })

    this.panels.forEach((panel) => {
      panel.hidden = true
    })

    document.documentElement.classList.remove('redesign-menu-open')
    document.body.classList.remove('redesign-menu-open')

    if (restoreFocus && this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.restoringFocus = true
      this.lastTrigger.focus()
      this.restoringFocus = false
    }
  }

  onTopTabPointerEnter (event) {
    if (!this.mediaQuery.matches || !this.hoverQuery.matches || event.pointerType !== 'mouse') return

    const tabHandle = event.currentTarget.dataset.topTabItem
    const subHandle = this.getFirstSubTab(tabHandle)
    const trigger = event.currentTarget.querySelector('.redesign-mega-nav__top-link') || event.currentTarget.querySelector('[data-top-tab]')

    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.openTimer)
    this.openTimer = window.setTimeout(() => {
      this.open(tabHandle, subHandle, trigger)
    }, this.HOVER_IN_DELAY)
  }

  onTopTabFocusIn (event) {
    if (!this.mediaQuery.matches || this.restoringFocus || !event.target.matches('.redesign-mega-nav__top-link')) return

    const tabHandle = event.currentTarget.dataset.topTabItem
    this.open(tabHandle, this.getFirstSubTab(tabHandle), event.target)
  }

  onTopTabClick (event) {
    if (!this.mediaQuery.matches) return

    event.preventDefault()

    const tabHandle = event.currentTarget.dataset.topTab
    const subHandle = this.getFirstSubTab(tabHandle)

    if (this.activeTab === tabHandle) {
      this.close()
      return
    }

    this.open(tabHandle, subHandle, event.currentTarget)
  }

  onSubTabPointerEnter (event) {
    if (!this.mediaQuery.matches || !this.hoverQuery.matches || event.pointerType !== 'mouse' || !this.activeTab) return

    const subHandle = event.currentTarget.dataset.subTabItem
    this.open(this.activeTab, subHandle, this.lastTrigger)
  }

  onSubTabFocusIn (event) {
    if (!this.mediaQuery.matches || !this.activeTab) return

    const subHandle = event.currentTarget.dataset.subTabItem
    this.open(this.activeTab, subHandle, this.lastTrigger)
  }

  onSubTabClick (event) {
    if (!this.mediaQuery.matches) return

    event.preventDefault()

    if (!this.activeTab) return

    const subHandle = event.currentTarget.dataset.subTab
    this.open(this.activeTab, subHandle, this.lastTrigger)
  }

  onRootPointerLeave (event) {
    if (!this.hoverQuery.matches || event.pointerType !== 'mouse') return

    window.clearTimeout(this.openTimer)
    window.clearTimeout(this.closeTimer)
    this.closeTimer = window.setTimeout(() => {
      this.close({ restoreFocus: false })
    }, this.HOVER_OUT_DELAY)
  }

  onRootPointerEnter () {
    window.clearTimeout(this.closeTimer)
  }

  onDocumentKeydown (event) {
    if (event.key === 'Escape' && this.activeTab) {
      this.close()
    }
  }

  onDocumentClick (event) {
    if (!this.activeTab) return

    if (!this.root.contains(event.target)) {
      this.close({ restoreFocus: false })
    }
  }

  onOverlayOpen (event) {
    if (!event.detail || event.detail.source === 'mega-nav') return

    this.close({ restoreFocus: false })
  }

  onMediaChange (event) {
    if (!event.matches) {
      this.close({ restoreFocus: false })
    }
  }

  onWindowResize () {
    if (!this.activeTab) return

    const activePanel = this.root.querySelector('[data-panel-for="'.concat(this.activeTab, '"]:not([hidden])'))

    if (activePanel) {
      this.updatePanelAvailableHeight(activePanel)
    }
  }

  updatePanelAvailableHeight (panel) {
    const viewportOffsetBottom = 24
    const panelTop = panel.getBoundingClientRect().top
    const availableHeight = Math.max(window.innerHeight - panelTop - viewportOffsetBottom, 240)

    panel.style.setProperty('--redesign-mega-nav-available-height', ''.concat(availableHeight, 'px'))
  }
}

export default RedesignMegaNav
